import { Injectable } from '@angular/core';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { User } from '../models';

import moment from 'moment';

export interface BadgePluginInterface {
  setBadge(options: { count: number }): Promise<{ count: number }>;
  clearBadge(): Promise<void>;
  getBadge(): Promise<{ count: number }>;
}

const NativeBadge = registerPlugin<BadgePluginInterface>('BadgePlugin');
const LAST_VIEWED_INVITES_KEY = 'a8_last_viewed_invites_time';

@Injectable({
  providedIn: 'root',
})
export class BadgeService {
  private lastCount: number = 0;
  private lastInvitesList: any[] = [];
  private cachedPendingCount: number = 0;

  async setBadge(count: number): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const safeCount = Math.max(0, Math.floor(count || 0));
    this.lastCount = safeCount;
    if (safeCount === 0) {
      try {
        await PushNotifications.removeAllDeliveredNotifications();
      } catch (e) {}
      try {
        await NativeBadge.clearBadge();
      } catch (err) {
        console.warn('Failed to clear native app badge:', err);
      }
    } else {
      try {
        await NativeBadge.setBadge({ count: safeCount });
      } catch (err) {
        console.warn('Failed to set native app badge:', err);
      }
    }
  }

  async clearBadge(): Promise<void> {
    await this.setBadge(0);
  }

  markInvitesViewed(): void {
    try {
      localStorage.setItem(LAST_VIEWED_INVITES_KEY, Date.now().toString());
    } catch (e) {}
  }

  getLastViewedInvitesTime(): number {
    try {
      const val = localStorage.getItem(LAST_VIEWED_INVITES_KEY);
      return val ? parseInt(val, 10) : 0;
    } catch (e) {
      return 0;
    }
  }

  getUnseenPendingInvitesCount(user?: User, pendingInvitesCount?: number, invitesList?: any[]): number {
    if (invitesList && Array.isArray(invitesList)) {
      this.lastInvitesList = invitesList;
    }
    if (pendingInvitesCount !== undefined) {
      this.cachedPendingCount = pendingInvitesCount;
    }

    const lastViewed = this.getLastViewedInvitesTime();
    if (!lastViewed) {
      return this.cachedPendingCount || 0;
    }

    const listToEvaluate = this.lastInvitesList;
    if (listToEvaluate && listToEvaluate.length > 0 && user?.id) {
      return listToEvaluate.filter((inv: any) => {
        const isPending = inv.status === 'pending' || inv.status === 'paid-cancel';
        const isForMe = inv.to?.id === user.id || (inv.to && String(inv.to.id) === String(user.id));
        const isUpcoming = !inv.datetime || moment(inv.datetime).isSameOrAfter(moment());
        const createdTime = inv.created ? new Date(inv.created).getTime() : 0;
        return isPending && isForMe && isUpcoming && createdTime > lastViewed;
      }).length;
    }

    return 0;
  }

  async syncBadge(user?: User, pendingInvitesCount?: number, invitesList?: any[]): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const unreadMessages = Math.max(0, user?.metrics?.unread_message || 0);
    const unreadInteractions = Math.max(0, user?.metrics?.unread_interactions || 0);
    const unseenInvites = this.getUnseenPendingInvitesCount(user, pendingInvitesCount, invitesList);

    const total = Math.max(0, unreadMessages + unreadInteractions + unseenInvites);
    await this.setBadge(total);
  }
}
